import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, Loader2, HandHeart, Banknote, Package, Search, Pencil, Trash2, Lock, Download, Users, PackageCheck, Info, AlertTriangle, Globe } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { BasarNav, BasarMetric } from "../components/basar/BasarNav";
import { GiftFormDialog } from "../components/basar/GiftFormDialog";
import { PosterButtons } from "../components/basar/PosterButtons";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { api, errorMessage, formatDateTime } from "../lib/api";
import { basarGiftStatusMeta, giftKindLabel, giftPaymentLabel, nok, shortDateTime, useMyBasar } from "../lib/basar";
import { useLang } from "../context/LanguageContext";

const FILTERS = [
  { key: "", label: "Alle" },
  { key: "to_collect", label: "Å hente" },
  { key: "item", label: "Tinggaver" },
  { key: "money", label: "Pengegaver" },
  { key: "pending", label: "Venter på Vipps" },
  { key: "cancelled", label: "Kansellerte" },
];

// /dashboard/basar/:id/gaver – gåveregisteret: alle gåver med givar (internt), manuell registrering, «motteke», redigering,
// gåveregister-PDF/CSV og premieplakat A4/A3. Gåveregisteret er grunnlaget for premielista/trekninga.
export default function BasarGiftsPage() {
  const { t } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { basar, emailEnabled, loading, error, setBasar } = useMyBasar(id);
  const [data, setData] = useState(null);
  const [listError, setListError] = useState(false);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(params.get("ny") === "1");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (error === "notfound") navigate("/dashboard/basar");
  }, [error, navigate]);

  const load = useCallback((silent = false) => {
    if (!silent) setListError(false);
    return api
      .get(`/my/basar/${id}/gifts`)
      .then((res) => setData(res.data))
      .catch(() => setListError(true));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const onFormOpenChange = (o) => {
    setFormOpen(o);
    if (!o && params.get("ny")) {
      params.delete("ny");
      setParams(params, { replace: true });
    }
  };
  const onSaved = (gift, fresh) => {
    if (fresh) setBasar(fresh);
    load(true);
  };

  const toggleCollected = async (g, collected) => {
    setBusy(g.id);
    try {
      const { data: res } = await api.patch(`/my/basar/${id}/gifts/${g.id}`, { collected });
      setBasar(res.basar);
      setData((d) => (d ? { ...d, gifts: d.gifts.map((x) => (x.id === g.id ? res.gift : x)) } : d));
      load(true);
      toast.success(collected ? t("Merket som mottatt") : t("Merking fjernet"));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    const g = confirmDelete;
    setConfirmDelete(null);
    if (!g) return;
    setBusy(g.id);
    try {
      const { data: res } = await api.delete(`/my/basar/${id}/gifts/${g.id}`);
      if (res.basar) setBasar(res.basar);
      toast.success(res.status === "cancelled" ? t("Betalingen er avbrutt") : t("Gaven er slettet"));
      load(true);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/my/basar/${id}/gifts/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "basar-gaver.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const gifts = data?.gifts || [];
  const summary = data?.summary || {};
  const published = !!basar?.draw_published_at;
  const rows = useMemo(() => {
    let out = gifts;
    const active = (g) => g.status === "registered" || g.status === "paid";
    if (filter === "to_collect") out = out.filter((g) => active(g) && g.kind === "item" && !g.collected_at);
    else if (filter === "item" || filter === "money") out = out.filter((g) => g.kind === filter && active(g));
    else if (filter === "pending") out = out.filter((g) => g.status === "pending_payment" || g.status === "processing");
    else if (filter === "cancelled") out = out.filter((g) => g.status === "cancelled");
    const needle = q.trim().toLowerCase();
    if (needle) out = out.filter((g) => [g.name, g.donor_name, g.donor_phone, g.donor_email, g.message, g.note].join(" ").toLowerCase().includes(needle));
    return out;
  }, [gifts, filter, q]);

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="basar-gifts-page">
        <BasarNav basar={basar} actions={basar && !published && <Button onClick={openNew} data-testid="basar-gifts-add-button"><Plus className="w-4 h-4 mr-1.5" /> {t("Registrer gave")}</Button>} />
        {loading || !basar || (!data && !listError) ? (
          <div className="space-y-6" data-testid="basar-loading-state"><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div><Skeleton className="h-72 rounded-xl" /></div>
        ) : listError ? (
          <Alert data-testid="basar-gifts-error">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t("Kunne ikke laste gaveregisteret")}</AlertTitle>
            <AlertDescription className="flex items-center justify-between gap-3">{t("Prøv igjen om litt.")} <Button size="sm" variant="outline" onClick={() => load()} data-testid="basar-gifts-retry-button">{t("Prøv igjen")}</Button></AlertDescription>
          </Alert>
        ) : (
          <>
            {/* Nøkkeltal */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="basar-gifts-metrics">
              <BasarMetric icon={HandHeart} label={t("Gaver")} value={summary.total ?? 0} testId="basar-gifts-metric-total" hint={t("{n} gevinster · verdi {amount}", { n: summary.copies ?? 0, amount: nok(summary.value || 0) })} />
              <BasarMetric icon={PackageCheck} label={t("Tinggaver å hente")} value={`${Math.max(0, (summary.items ?? 0) - (summary.items_collected ?? 0))} / ${summary.items ?? 0}`} testId="basar-gifts-metric-collect" hint={t("{n} mottatt", { n: summary.items_collected ?? 0 })} />
              <BasarMetric icon={Banknote} label={t("Pengegaver")} value={nok(summary.money_total || 0)} testId="basar-gifts-metric-money" hint={summary.money === 1 ? t("{n} pengegave", { n: 1 }) : t("{n} pengegaver", { n: summary.money ?? 0 })} />
              <BasarMetric icon={Globe} label={t("Fra publikum")} value={summary.public ?? 0} testId="basar-gifts-metric-public" hint={summary.pending > 0 ? t("{n} venter på Vipps", { n: summary.pending }) : t("via «Gi premie» på basarsiden")} />
            </div>

            {/* Verktøylinje: plakat/PDF + filter + søk */}
            <Card className="border-border">
              <CardContent className="p-4 sm:p-5 space-y-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="space-y-1 min-w-0">
                    <p className="font-heading font-semibold">{t("Skriv ut")}</p>
                    <p className="text-xs text-muted-foreground">{t("Gaveregisteret er den interne listen med givere (til innhenting). Plakaten viser premiene, trekningstidspunkt og QR-kode – aldri giverne.")}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <PosterButtons basarId={id} withRegister />
                    <Button variant="secondary" size="sm" onClick={exportCsv} data-testid="basar-gifts-export-button"><Download className="w-4 h-4 mr-1.5" /> {t("CSV")}</Button>
                  </div>
                </div>
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between border-t border-border pt-4">
                  <Tabs value={filter} onValueChange={setFilter} className="min-w-0">
                    <TabsList className="h-auto flex-wrap justify-start" data-testid="basar-gifts-filter-tabs">
                      {FILTERS.map((f) => <TabsTrigger key={f.key || "all"} value={f.key} className="text-xs sm:text-sm" data-testid={`basar-gifts-filter-${f.key || "all"}`}>{t(f.label)}</TabsTrigger>)}
                    </TabsList>
                  </Tabs>
                  <div className="relative md:w-64">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Søk gave, giver, mobil …")} className="pl-9 h-9" data-testid="basar-gifts-search-input" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {published && <Alert data-testid="basar-gifts-published-lock"><Lock className="h-4 w-4" /><AlertDescription>{t("Trekningen er publisert. Nye gaver kan ikke registreres, men du kan fortsatt merke gaver som mottatt.")}</AlertDescription></Alert>}

            {/* Liste */}
            {rows.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center space-y-3" data-testid="basar-gifts-empty">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"><HandHeart className="w-6 h-6" /></span>
                <p className="font-heading font-semibold">{gifts.length === 0 ? t("Ingen gaver ennå") : t("Ingen gaver passet filteret")}</p>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">{gifts.length === 0 ? t("Gaver fra «Gi premie» på basarsiden havner her automatisk. Gaver dere får utenom nettsiden registrerer du manuelt – de blir premier med en gang.") : t("Prøv et annet filter eller søk.")}</p>
                {gifts.length === 0 && !published && <Button onClick={openNew} variant="outline" data-testid="basar-gifts-empty-add-button"><Plus className="w-4 h-4 mr-1.5" /> {t("Registrer gave")}</Button>}
              </div>
            ) : (
              <div className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid="basar-gifts-list">
                <div className="hidden md:grid grid-cols-[3rem_minmax(0,2.2fr)_minmax(0,2fr)_minmax(0,1.3fr)_7rem] gap-3 px-4 py-2 text-xs font-medium text-muted-foreground">
                  <span>{t("Mottatt")}</span><span>{t("Gave")}</span><span>{t("Giver")}</span><span>{t("Status")}</span><span className="text-right">{t("Handling")}</span>
                </div>
                {rows.map((g) => {
                  const st = basarGiftStatusMeta(g);
                  const active = g.status === "registered" || g.status === "paid";
                  const item = g.kind === "item";
                  const vippsPaid = g.status === "paid" && g.payment_method === "vipps" && g.source === "public";
                  return (
                    <div key={g.id} className="grid grid-cols-[3rem_minmax(0,1fr)] md:grid-cols-[3rem_minmax(0,2.2fr)_minmax(0,2fr)_minmax(0,1.3fr)_7rem] gap-x-3 gap-y-2 px-4 py-3 items-start" data-testid="basar-gift-row" data-kind={g.kind} data-status={g.status}>
                      <div className="pt-0.5 flex justify-center">
                        {item && active ? (
                          <Checkbox checked={!!g.collected_at} disabled={busy === g.id} onCheckedChange={(v) => toggleCollected(g, !!v)} aria-label={t("Mottatt")} className="h-5 w-5" data-testid="basar-gift-collected-checkbox" />
                        ) : (
                          <span className="inline-flex items-center justify-center w-5 h-5 text-muted-foreground" aria-hidden>{item ? <Package className="w-4 h-4" /> : <Banknote className="w-4 h-4 text-[hsl(var(--warning))]" />}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium leading-snug" data-testid="basar-gift-name">{g.name}{item && g.count > 1 ? <span className="text-muted-foreground"> × {g.count}</span> : ""}</p>
                        <p className="text-xs text-muted-foreground tabular-nums">
                          {giftKindLabel(g.kind)}{g.value ? ` · ${item && g.count > 1 ? t("{amount} per stk", { amount: nok(g.value) }) : nok(g.value)}` : ""}{giftPaymentLabel(g) ? ` · ${giftPaymentLabel(g)}` : ""}
                          {g.collected_at ? ` · ${t("mottatt {date}", { date: shortDateTime(g.collected_at) })}` : ""}
                        </p>
                        {g.description && <p className="text-xs text-muted-foreground truncate">{g.description}</p>}
                        {g.prize_missing && <p className="text-xs text-[hsl(var(--warning))] inline-flex items-center gap-1 mt-0.5" data-testid="basar-gift-prize-missing"><AlertTriangle className="w-3.5 h-3.5" /> {t("Ikke i premielisten (premien ble fjernet)")}</p>}
                        {g.prize_name && g.prize_name !== g.name && <p className="text-xs text-muted-foreground mt-0.5">{t("Premie: {name}", { name: g.prize_name })}</p>}
                      </div>
                      <div className="min-w-0 col-start-2 md:col-start-auto text-sm">
                        <p className="font-medium truncate" data-testid="basar-gift-donor">{g.donor_name || <span className="text-muted-foreground font-normal">{t("Ukjent giver")}</span>}{g.donor_kind === "company" && g.donor_name ? <span className="text-xs text-muted-foreground font-normal"> · {t("Bedrift")}</span> : ""}</p>
                        <p className="text-xs text-muted-foreground truncate">{[g.donor_phone, g.donor_email].filter(Boolean).join(" · ") || "–"}</p>
                        {(g.message || g.note) && <p className="text-xs text-muted-foreground truncate" title={g.message || g.note}>{g.message ? `«${g.message}»` : g.note}</p>}
                      </div>
                      <div className="col-start-2 md:col-start-auto flex flex-wrap items-center gap-1.5 text-xs">
                        <Badge className={st.className} data-testid="basar-gift-status-badge">{st.label}</Badge>
                        <Badge variant="outline" className="text-[11px]">{g.source === "public" ? t("Fra publikum") : t("Manuelt")}</Badge>
                        <span className="text-muted-foreground w-full md:w-auto">{formatDateTime(g.created_at)}</span>
                      </div>
                      <div className="col-start-2 md:col-start-auto flex md:justify-end items-center gap-1">
                        {active && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing(g); setFormOpen(true); }} aria-label={t("Rediger gave")} data-testid="basar-gift-edit-button"><Pencil className="w-4 h-4" /></Button>
                        )}
                        {!published && !vippsPaid && g.status !== "cancelled" && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]" onClick={() => setConfirmDelete(g)} disabled={busy === g.id} aria-label={g.status === "pending_payment" ? t("Avbryt betaling") : t("Slett gave")} data-testid="basar-gift-delete-button">{busy === g.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}</Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <Alert data-testid="basar-gifts-privacy-note">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">{t("Giverne vises aldri offentlig – verken på basarsiden eller plakaten. Pengegaver via Vipps går direkte til deres konto og kan ikke slettes her; tilbakebetaling gjøres i Vipps-portalen.")}</AlertDescription>
            </Alert>
          </>
        )}
      </div>

      {basar && <GiftFormDialog basar={basar} open={formOpen} onOpenChange={onFormOpenChange} gift={editing} onSaved={onSaved} emailEnabled={emailEnabled} />}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="basar-gift-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDelete?.status === "pending_payment" ? t("Avbryte betalingen?") : t("Slette gaven?")}</AlertDialogTitle>
            <AlertDialogDescription>{confirmDelete?.status === "pending_payment" ? t("Vipps-betalingen avbrytes og gaven merkes som kansellert.") : t("Gaven fjernes fra gaveregisteret, og premien den ga fjernes fra premielisten. Dette kan ikke angres.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="basar-gift-delete-cancel">{t("Avbryt")}</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:opacity-95" data-testid="basar-gift-delete-confirm">{t("Ja, fjern")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
